import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    ArrowLeft,
    ChevronDown,
    ChevronRight,
    FileText,
    Gavel,
    Loader2,
    MessageSquareWarning,
    Scale,
    SquareArrowOutUpRight,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { cn } from '@apps/web/lib/utils';
import {
    humanLabel,
    justiceVerdictMeta,
    priorityBadgeClass,
    STATUS_LABELS,
    statusBadgeClass,
    TRIGGER_TYPE_LABELS,
} from '@apps/web/routes/_app/admin/agent-court/agent_court_display';
import { PlaybookReviewDialog } from '@apps/web/routes/_app/admin/agent-court/playbook_review_dialog';
import { useAdminAgentCourtCase } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

type AgentCourtCaseDetail = components['schemas']['AgentCourtCaseDetail'];
type AgentCourtRecommendation =
    components['schemas']['AgentCourtRecommendationRead'];
type AgentCourtRecommendationGroup =
    components['schemas']['AgentCourtRecommendationGroupRead'];

function formatDateTime(value: string | null | undefined): string {
    return value ? format(new Date(value), 'MMM d, yyyy h:mm a') : '—';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function getNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getUsageNumber(usage: unknown, key: string): number | null {
    if (!isRecord(usage)) {
        return null;
    }
    return getNumber(usage[key]);
}

function formatTokenCount(value: number): string {
    return value.toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });
}

function formatJusticeUsage(usage: unknown): string | null {
    const totalTokens = getUsageNumber(usage, 'total_tokens');
    const inputTokens = getUsageNumber(usage, 'input_tokens');
    const outputTokens = getUsageNumber(usage, 'output_tokens');
    const cacheReadTokens = getUsageNumber(usage, 'cache_read_tokens');
    const cacheCreationTokens = getUsageNumber(usage, 'cache_creation_tokens');
    const parts = [
        inputTokens === null ? null : `${formatTokenCount(inputTokens)} in`,
        outputTokens === null ? null : `${formatTokenCount(outputTokens)} out`,
        cacheReadTokens === null
            ? null
            : `${formatTokenCount(cacheReadTokens)} cache read`,
        cacheCreationTokens === null
            ? null
            : `${formatTokenCount(cacheCreationTokens)} cache write`,
    ].filter((part): part is string => part !== null);
    if (totalTokens !== null) {
        return `${formatTokenCount(totalTokens)} tokens${
            parts.length > 0 ? ` (${parts.join(' / ')})` : ''
        }`;
    }
    if (parts.length === 0) {
        return null;
    }
    return `${parts.join(' / ')} tokens`;
}

function formatCostUsd(value: number | null | undefined): string | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    if (value === 0) {
        return '$0.00';
    }
    if (value < 0.01) {
        return `$${value.toFixed(4)}`;
    }
    return `$${value.toFixed(2)}`;
}

// The case list threads its own view state (page, root-cause filter,
// arrived-from-analysis flag) through this route so the back arrow
// restores exactly the list the user drilled in from. Entries from other
// pages (the trace page's case links) carry no search and fall back to
// the plain list.
type AgentCourtCaseSearch = {
    readonly page?: number;
    readonly root_cause?: string;
    readonly from_analysis?: boolean;
};

export const Route = createFileRoute('/_app/admin/agent-court/$caseId')({
    component: AgentCourtCaseDetailPage,
    head: () => ({ meta: [{ title: 'Agent Court Case' }] }),
    validateSearch: (search: Record<string, unknown>): AgentCourtCaseSearch => {
        const page = Number(search['page']);
        const rootCause = search['root_cause'];
        return {
            page:
                Number.isFinite(page) && page >= 1
                    ? Math.floor(page)
                    : undefined,
            root_cause:
                typeof rootCause === 'string' && rootCause.length > 0
                    ? rootCause
                    : undefined,
            from_analysis: search['from_analysis'] === true ? true : undefined,
        };
    },
});

function AgentCourtCaseDetailPage() {
    const { caseId } = Route.useParams();
    const {
        data: agentCourtCase,
        isLoading,
        error,
        mutate,
    } = useAdminAgentCourtCase(caseId);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-muted-foreground py-12 text-center text-sm">
                Failed to load Agent Court case.
            </div>
        );
    }

    if (!agentCourtCase) {
        return (
            <div className="text-muted-foreground py-12 text-center text-sm">
                Agent Court case not found.
            </div>
        );
    }

    return (
        <div className="mx-auto flex max-w-4xl flex-col gap-4 p-6">
            <CaseHeader agentCourtCase={agentCourtCase} />
            <VerdictBanner agentCourtCase={agentCourtCase} />
            <RecommendationsCard
                agentCourtCase={agentCourtCase}
                onReviewed={() => void mutate()}
            />
            <LlmJudgeCard agentCourtCase={agentCourtCase} />
            <UserFeedbackCard agentCourtCase={agentCourtCase} />
            <ContextReviewedCard agentCourtCase={agentCourtCase} />
            <SourceLinksCard agentCourtCase={agentCourtCase} />
        </div>
    );
}

function CaseHeader({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    const braintrustLinks = [
        { label: 'Braintrust case', url: agentCourtCase.braintrust_run_url },
        {
            label: 'Braintrust Justice run',
            url: agentCourtCase.justice_run?.braintrust_run_url ?? null,
        },
    ].filter((link) => link.url !== null);

    const listSearch = Route.useSearch();
    const router = useRouter();
    const navigate = Route.useNavigate();
    return (
        <div className="flex items-start gap-3">
            {/* Pop history when possible instead of pushing the list
                again: a push would grow the stack (analysis → list →
                case → list…), making the list page's own back arrow
                bounce into this case instead of reaching Agent
                Analysis. The threaded-search navigate covers direct
                entries (new tab, pasted link). */}
            <Button
                variant="ghost"
                size="sm"
                aria-label="Back to the case list"
                onClick={() => {
                    if (router.history.canGoBack()) {
                        router.history.back();
                        return;
                    }
                    void navigate({
                        to: '/admin/agent-court',
                        search: {
                            page: listSearch.page ?? 1,
                            root_cause: listSearch.root_cause,
                            from_analysis: listSearch.from_analysis,
                        },
                    });
                }}
            >
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <Gavel className="h-5 w-5" />
                    <h1 className="text-lg font-semibold">
                        {agentCourtCase.organization_internal_name ??
                            agentCourtCase.organization_name ??
                            'Unknown org'}
                    </h1>
                    <Badge
                        variant="secondary"
                        className={cn(
                            'text-xs',
                            statusBadgeClass(agentCourtCase.status),
                        )}
                    >
                        {humanLabel(STATUS_LABELS, agentCourtCase.status)}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                        {humanLabel(
                            TRIGGER_TYPE_LABELS,
                            agentCourtCase.trigger_type,
                        )}
                    </Badge>
                </div>
                <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span>
                        Created {formatDateTime(agentCourtCase.created_at)}
                    </span>
                    {agentCourtCase.justice_completed_at ? (
                        <span>
                            Justice completed{' '}
                            {formatDateTime(
                                agentCourtCase.justice_completed_at,
                            )}
                        </span>
                    ) : null}
                    {braintrustLinks.map((link) => (
                        <a
                            key={link.label}
                            href={link.url ?? undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary inline-flex items-center gap-1 underline"
                        >
                            {link.label}
                            <SquareArrowOutUpRight className="h-3 w-3" />
                        </a>
                    ))}
                </div>
                {agentCourtCase.failure_reason ? (
                    <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/50 dark:text-red-300">
                        {agentCourtCase.failure_reason}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function VerdictBanner({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    const justiceRun = agentCourtCase.justice_run;
    if (!justiceRun) {
        return (
            <div className="bg-muted/40 flex items-center gap-3 rounded-xl border p-4">
                <Scale className="text-muted-foreground h-5 w-5 shrink-0" />
                <div>
                    <div className="text-sm font-semibold">
                        No Justice verdict yet
                    </div>
                    <p className="text-muted-foreground text-xs">
                        Status:{' '}
                        {humanLabel(STATUS_LABELS, agentCourtCase.status)}
                    </p>
                </div>
            </div>
        );
    }

    const verdictMeta = justiceVerdictMeta(justiceRun.verdict);
    const usageSummary = formatJusticeUsage(justiceRun.usage);
    const estimatedCost = formatCostUsd(justiceRun.estimated_cost_usd);
    const stats: Array<{ readonly label: string; readonly value: string }> = [
        {
            label: 'Target scope',
            value: justiceRun.recommended_target_scope ?? '—',
        },
        {
            label: 'Recommendations',
            value: String(agentCourtCase.recommendations.length),
        },
    ];
    if (justiceRun.model) {
        stats.push({ label: 'Model', value: justiceRun.model });
    }
    if (usageSummary) {
        stats.push({ label: 'Usage', value: usageSummary });
    }
    if (estimatedCost) {
        stats.push({ label: 'Est. cost', value: estimatedCost });
    }

    return (
        <section
            className={cn(
                'rounded-xl border p-4',
                verdictMeta?.bannerClassName ?? 'bg-muted/40',
            )}
        >
            <div className="flex items-center gap-2">
                <span
                    className={cn(
                        'h-2.5 w-2.5 shrink-0 rounded-full',
                        verdictMeta?.dotClassName ?? 'bg-slate-400',
                    )}
                />
                <h2 className="text-base font-semibold">
                    {verdictMeta?.label ?? 'Justice verdict'}
                </h2>
                <Badge
                    variant="outline"
                    className={cn(
                        'text-xs',
                        statusBadgeClass(justiceRun.status),
                    )}
                >
                    {humanLabel(STATUS_LABELS, justiceRun.status)}
                </Badge>
            </div>
            {verdictMeta?.description ? (
                <p className="text-muted-foreground mt-1 text-xs">
                    {verdictMeta.description}
                </p>
            ) : null}
            {justiceRun.failure_pattern ? (
                <p className="mt-3 text-sm font-medium">
                    {justiceRun.failure_pattern}
                </p>
            ) : null}
            {justiceRun.failure_category ? (
                <p className="text-muted-foreground mt-1 text-xs">
                    Category:{' '}
                    <span className="font-mono">
                        {justiceRun.failure_category}
                    </span>
                </p>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {stats.map((stat) => (
                    <div key={stat.label} className="text-xs">
                        <div className="text-muted-foreground">
                            {stat.label}
                        </div>
                        <div className="font-medium">{stat.value}</div>
                    </div>
                ))}
            </div>
            {justiceRun.error_message ? (
                <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/50 dark:text-red-300">
                    {justiceRun.error_message}
                </div>
            ) : null}
            <div className="mt-3">
                <JsonBlock
                    label="Raw Justice output"
                    value={justiceRun.justice_output}
                />
            </div>
        </section>
    );
}

function RecommendationsCard({
    agentCourtCase,
    onReviewed,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
    readonly onReviewed: () => void;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <Gavel className="h-4 w-4" />
                    Recommendations ({agentCourtCase.recommendations.length})
                </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                {agentCourtCase.recommendation_groups.length === 0 ? (
                    <span className="text-muted-foreground text-sm">
                        No recommendations.
                    </span>
                ) : (
                    agentCourtCase.recommendation_groups.map((group) => (
                        <RecommendationGroup
                            key={`${group.target_type}:${group.target_identifier}`}
                            group={group}
                            onReviewed={onReviewed}
                        />
                    ))
                )}
            </CardContent>
        </Card>
    );
}

function RecommendationGroup({
    group,
    onReviewed,
}: {
    readonly group: AgentCourtRecommendationGroup;
    readonly onReviewed: () => void;
}) {
    return (
        <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 border-b pb-2">
                <Badge variant="secondary" className="text-xs">
                    {group.target_type}
                </Badge>
                <span className="font-mono text-sm font-medium">
                    {group.target_identifier}
                </span>
                <span className="text-muted-foreground text-xs">
                    {group.recommendations.length} recommendation
                    {group.recommendations.length === 1 ? '' : 's'}
                </span>
            </div>
            {group.recommendations.map((recommendation) => (
                <RecommendationCard
                    key={recommendation.id}
                    recommendation={recommendation}
                    onReviewed={onReviewed}
                />
            ))}
        </section>
    );
}

const REVIEW_STATUS_BADGE_CLASS: Record<string, string> = {
    approved:
        'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    applied:
        'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
};

function RecommendationCard({
    recommendation,
    onReviewed,
}: {
    readonly recommendation: AgentCourtRecommendation;
    readonly onReviewed: () => void;
}) {
    // Playbook targets are live prod data and are reviewed/applied in-product;
    // other targets are systemic code changes reviewed via GitHub.
    const isPlaybookTarget = recommendation.target_type === 'playbook';
    return (
        <div className="overflow-hidden rounded-lg border">
            <div className="bg-muted/40 flex flex-wrap items-center gap-2 border-b px-3 py-2">
                <Badge
                    variant="secondary"
                    className={cn(
                        'text-xs',
                        priorityBadgeClass(recommendation.priority),
                    )}
                >
                    {recommendation.priority} priority
                </Badge>
                {recommendation.confidence_label ? (
                    <Badge variant="outline" className="text-xs">
                        {recommendation.confidence_label} confidence
                    </Badge>
                ) : null}
                {recommendation.review_status !== 'pending' ? (
                    <Badge
                        variant="secondary"
                        className={cn(
                            'text-xs',
                            REVIEW_STATUS_BADGE_CLASS[
                                recommendation.review_status
                            ],
                        )}
                    >
                        {recommendation.review_status}
                    </Badge>
                ) : null}
                {isPlaybookTarget ? (
                    <div className="ml-auto">
                        <PlaybookReviewDialog
                            recommendationId={recommendation.id}
                            targetIdentifier={recommendation.target_identifier}
                            onReviewed={onReviewed}
                        />
                    </div>
                ) : null}
            </div>
            <div className="flex flex-col gap-3 p-3">
                <p className="text-sm font-medium">
                    {recommendation.change_summary}
                </p>
                <LabeledText
                    label="Rationale"
                    value={recommendation.rationale}
                />
                <LabeledText
                    label="Why this target"
                    value={recommendation.why_this_target}
                />
                <CollapsibleText
                    label="Proposed replacement content"
                    value={recommendation.proposed_new_content}
                />
                <JsonBlock label="Evidence" value={recommendation.evidence} />
            </div>
        </div>
    );
}

function LlmJudgeCard({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    if (
        agentCourtCase.llm_judge_result === null ||
        agentCourtCase.llm_judge_result === undefined
    ) {
        return null;
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">LLM Judge result</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 text-sm">
                {agentCourtCase.llm_judge_verdict ? (
                    <Badge variant="secondary" className="w-fit">
                        {agentCourtCase.llm_judge_verdict}
                    </Badge>
                ) : (
                    <span className="text-muted-foreground text-xs">
                        No headline verdict; see raw result.
                    </span>
                )}
                <JsonBlock
                    label="Raw LLM Judge result"
                    value={agentCourtCase.llm_judge_result}
                />
            </CardContent>
        </Card>
    );
}

function UserFeedbackCard({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    const feedback = agentCourtCase.user_feedback;
    if (!feedback) {
        return null;
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <MessageSquareWarning className="h-4 w-4" />
                    User feedback
                </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                    {feedback.label ? (
                        <Badge
                            variant="secondary"
                            className={cn(
                                'text-xs',
                                feedback.label === 'bad' &&
                                    'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
                                feedback.label === 'good' &&
                                    'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
                            )}
                        >
                            {feedback.label}
                        </Badge>
                    ) : null}
                    <span className="text-muted-foreground text-xs">
                        {feedback.created_by_user_name ?? 'Unknown user'}
                    </span>
                    <span className="text-muted-foreground text-xs">
                        {formatDateTime(feedback.created_at)}
                    </span>
                    {feedback.braintrust_run_url ? (
                        <a
                            href={feedback.braintrust_run_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary inline-flex items-center gap-1 text-xs underline"
                        >
                            Braintrust
                            <SquareArrowOutUpRight className="h-3 w-3" />
                        </a>
                    ) : null}
                </div>
                {feedback.comment ? (
                    <p className="whitespace-pre-wrap">{feedback.comment}</p>
                ) : (
                    <span className="text-muted-foreground">
                        No comment provided.
                    </span>
                )}
            </CardContent>
        </Card>
    );
}

function ContextReviewedCard({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    const context = agentCourtCase.justice_run?.context_reviewed;
    if (!context) {
        return null;
    }
    const documentGroups: Array<{
        readonly label: string;
        readonly documents: Array<unknown>;
    }> = [
        { label: 'Prompts', documents: context.prompts_used },
        { label: 'Skills', documents: context.skills_used },
        { label: 'Playbooks', documents: context.playbooks_used },
        { label: 'Tool schemas', documents: context.tool_schemas },
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4" />
                    Context reviewed
                </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 text-sm">
                {context.trace_summary ? (
                    <p className="text-muted-foreground whitespace-pre-wrap">
                        {context.trace_summary}
                    </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                    <CountBadge
                        label="Relevant turns"
                        value={context.relevant_turn_count}
                    />
                    <CountBadge
                        label="Actions"
                        value={context.action_ledger_count}
                    />
                    {documentGroups.map((group) => (
                        <CountBadge
                            key={group.label}
                            label={group.label}
                            value={group.documents.length}
                        />
                    ))}
                </div>
                {context.expansion_audit.length > 0 ? (
                    <div className="overflow-x-auto rounded-md border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50 text-muted-foreground">
                                <tr>
                                    <th className="px-2 py-1 text-left font-medium">
                                        Hook
                                    </th>
                                    <th className="px-2 py-1 text-left font-medium">
                                        Identifier
                                    </th>
                                    <th className="px-2 py-1 text-left font-medium">
                                        Status
                                    </th>
                                    <th className="px-2 py-1 text-right font-medium">
                                        Chars
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {context.expansion_audit.map((entry, index) => (
                                    <ExpansionAuditRow
                                        key={index}
                                        entry={entry}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function ExpansionAuditRow({ entry }: { readonly entry: unknown }) {
    const record = isRecord(entry) ? entry : {};
    const status = getString(record['status']);
    return (
        <tr className="border-t">
            <td className="px-2 py-1">{getString(record['hook']) ?? '—'}</td>
            <td className="px-2 py-1 font-mono">
                {getString(record['identifier']) ?? '—'}
            </td>
            <td className="px-2 py-1">
                {status ? (
                    <Badge
                        variant="outline"
                        className={cn(
                            'text-xs',
                            status === 'included' &&
                                'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
                            status === 'truncated' &&
                                'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                            (status === 'missing' || status === 'error') &&
                                'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
                        )}
                    >
                        {status}
                    </Badge>
                ) : (
                    '—'
                )}
            </td>
            <td className="px-2 py-1 text-right tabular-nums">
                {getNumber(record['character_count']) ?? '—'}
            </td>
        </tr>
    );
}

function CountBadge({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number;
}) {
    return (
        <Badge variant="outline">
            <span>{label}</span>
            <span className="ml-1 font-mono">{value}</span>
        </Badge>
    );
}

function SourceLinksCard({
    agentCourtCase,
}: {
    readonly agentCourtCase: AgentCourtCaseDetail;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">Source</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
                {agentCourtCase.source_agent_conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{
                            conversationId:
                                agentCourtCase.source_agent_conversation_id,
                        }}
                        className="text-primary inline-flex w-fit items-center gap-1 underline"
                    >
                        View source agent trace
                        <SquareArrowOutUpRight className="h-3 w-3" />
                    </Link>
                ) : (
                    <span className="text-muted-foreground">
                        No source agent trace linked.
                    </span>
                )}
                <IdField
                    label="Source run"
                    value={agentCourtCase.source_phoebe_agent_run_id}
                />
                <IdField
                    label="Source review note"
                    value={agentCourtCase.source_review_note_id}
                />
                <IdField
                    label="Source review annotation"
                    value={agentCourtCase.source_review_annotation_id}
                />
                <IdField
                    label="Source feedback"
                    value={agentCourtCase.source_feedback_id}
                />
            </CardContent>
        </Card>
    );
}

function IdField({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string | null;
}) {
    if (!value) {
        return null;
    }
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-mono">{value}</span>
        </div>
    );
}

function LabeledText({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string | null | undefined;
}) {
    if (!value) {
        return null;
    }
    return (
        <div className="flex flex-col gap-1">
            <span className="text-muted-foreground text-xs font-medium">
                {label}
            </span>
            <p className="whitespace-pre-wrap">{value}</p>
        </div>
    );
}

function CollapsibleText({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    const [open, setOpen] = useState(false);
    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild={true}>
                <button
                    type="button"
                    className="hover:bg-muted/60 flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs"
                >
                    <span className="flex items-center gap-1 font-medium">
                        {open ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                        {label}
                    </span>
                    <span className="text-muted-foreground">
                        {value.length.toLocaleString()} chars
                    </span>
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <pre className="bg-muted mt-2 max-h-[420px] overflow-auto rounded p-3 text-xs whitespace-pre-wrap">
                    {value}
                </pre>
            </CollapsibleContent>
        </Collapsible>
    );
}

function JsonBlock({
    label,
    value,
    defaultOpen = false,
}: {
    readonly label: string;
    readonly value: unknown;
    readonly defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    if (value === null || value === undefined) {
        return null;
    }
    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild={true}>
                <button
                    type="button"
                    className="text-muted-foreground flex items-center gap-1 text-xs"
                >
                    {open ? (
                        <ChevronDown className="h-3 w-3" />
                    ) : (
                        <ChevronRight className="h-3 w-3" />
                    )}
                    {label}
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <JsonContent value={value} />
            </CollapsibleContent>
        </Collapsible>
    );
}

function JsonContent({ value }: { readonly value: unknown }): ReactNode {
    return (
        <pre className="bg-muted mt-2 max-h-[360px] overflow-auto rounded p-2 text-[11px]">
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}
